// ---------------------------------------------------------------------------
// Mobile page task contracts.
//
// One self-contained per-page registry backs the mobile shell and the
// v5Content contract layer below. Each entry carries the shell-critical runtime
// fields (shellMode / bottomNav / route / domain), the page's product question,
// and its audit metadata. `auditOwner` is the page's provenance tag; the app
// shell still reads it to keep the three task-hub pages ("P0C") header-less.
//
// The v5Content section further down is the active contract system: every page
// opts in and `getEffectiveMobilePageContract` resolves to it. This registry
// supplies the fields v5 derives (bottomNav / domain / route / shellMode /
// productQuestion / requiredStates).
// ---------------------------------------------------------------------------

export type MobilePageID =
  | "dailySchedule"
  | "dashboard"
  | "draft"
  | "draftHistory"
  | "draftLottery"
  | "draftScouting"
  | "freeAgents"
  | "gameLog"
  | "history"
  | "leaders"
  | "leagueDashboard"
  | "liveGame"
  | "marketHub"
  | "negotiationList"
  | "newLeague"
  | "news"
  | "player"
  | "playerRatings"
  | "playerStats"
  | "playoffs"
  | "protectPlayers"
  | "roster"
  | "standings"
  | "teamFinances"
  | "teamStats"
  | "trade";

export type MobilePageAuditOwner = "P0C" | "P1V4" | "P2V2" | "P3V1";

export type MobilePageShellMode = "standard" | "fullscreen" | "outsideLeague";

export type MobilePageDepthPolicy =
  | "default"
  | "summaryFirstAdvancedSecondLevel"
  | "storyFirstAdvancedArchive";

export type MobilePageContract = {
  auditOwner: MobilePageAuditOwner;
  bottomNav: boolean;
  depthPolicy: MobilePageDepthPolicy;
  disallowedMobilePatterns: readonly string[];
  domain: string;
  globalFixedCTA: false;
  globalScoreStrip: false;
  pageID: MobilePageID;
  primaryActionPolicy: string;
  productQuestion: string;
  requiredEvidence: readonly string[];
  requiredStates: readonly string[];
  route: readonly string[];
  shellMode: MobilePageShellMode;
  template: string;
};

export const mobilePageTaskContracts = {
  dailySchedule: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "games",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "dailySchedule",
    primaryActionPolicy: "singleSolidCTA",
    productQuestion: "What is my matchday action or no-game state?",
    requiredEvidence: [
      "games-root-task-page",
      "MyGameHeroModel",
      "matchday-hero",
      "matchday-empty-state",
      "rest-day-next-game",
      "other-games-secondary",
      "postgame-result-hero",
    ],
    requiredStates: ["empty", "completed", "returnContext"],
    route: ["daily_schedule"],
    shellMode: "standard",
    template: "matchday",
  },
  dashboard: {
    auditOwner: "P1V4",
    bottomNav: false,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "outsideLeague",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "dashboard",
    primaryActionPolicy: "singleSolidCTA",
    productQuestion: "Should I continue a save or start my first league?",
    requiredEvidence: [
      "recentLeagueViewModel",
      "ContinueLeagueHero",
      "StartLeagueActions",
      "LeagueLibraryLink",
      "single-primary-cta",
      "new-user-empty-state",
    ],
    requiredStates: ["empty", "completed", "returnContext"],
    route: [],
    shellMode: "outsideLeague",
    template: "entryHero",
  },
  draft: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "market",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "draft",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "At this pick, who should I choose or can I safely advance?",
    requiredEvidence: [
      "draft-clock-hero",
      "candidate-compare-cards",
      "team-need-recommendation",
      "pick-confirmation-impact",
    ],
    requiredStates: ["blocked", "completed", "returnContext"],
    route: ["draft"],
    shellMode: "standard",
    template: "draftClock",
  },
  draftHistory: {
    auditOwner: "P3V1",
    bottomNav: true,
    depthPolicy: "storyFirstAdvancedArchive",
    disallowedMobilePatterns: [
      "nativeSelect",
      "firstScreenDataTable",
      "genericFieldStack",
      "equalWeightLongCards",
      "unscopedSearchOverlay",
      "unmanagedEditControls",
      "untranslatedEventText",
    ],
    domain: "draftArchive",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "draftHistory",
    primaryActionPolicy: "readonlyContext",
    productQuestion: "Which draft picks became the story of this class?",
    requiredEvidence: [
      "recent-class-summary",
      "best-pick",
      "biggest-steal",
      "underperformer",
      "natural-zero-copy",
      "advanced-table-depth",
    ],
    requiredStates: ["empty", "hiddenRatings", "advancedArchive", "returnContext"],
    route: ["draft_history"],
    shellMode: "standard",
    template: "draftStory",
  },
  draftLottery: {
    auditOwner: "P2V2",
    bottomNav: true,
    depthPolicy: "summaryFirstAdvancedSecondLevel",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
      "firstScreenDataTable",
      "duplicateTerminalAction",
      "unscopedSearchOverlay",
    ],
    domain: "draftPlanning",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "draftLottery",
    primaryActionPolicy: "readonlyNextStep",
    productQuestion: "What are my lottery odds, expected pick range, and final result?",
    requiredEvidence: [
      "user-team-highlight",
      "best-worst-expected-pick",
      "probability-bars",
      "reveal-next-step",
    ],
    requiredStates: ["empty", "completed", "returnContext", "advancedDepth"],
    route: ["draft_lottery"],
    shellMode: "standard",
    template: "lotteryReveal",
  },
  draftScouting: {
    auditOwner: "P2V2",
    bottomNav: true,
    depthPolicy: "summaryFirstAdvancedSecondLevel",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
      "firstScreenDataTable",
      "duplicateTerminalAction",
      "unscopedSearchOverlay",
    ],
    domain: "draftPlanning",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "draftScouting",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "Which prospects fit my team needs well enough to shortlist?",
    requiredEvidence: [
      "team-need-card",
      "shortlist-first",
      "confidence-role-risk-fit",
      "scouting-progress",
    ],
    requiredStates: ["empty", "blocked", "returnContext", "advancedDepth"],
    route: ["draft_scouting"],
    shellMode: "standard",
    template: "scoutingShortlist",
  },
  freeAgents: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "market",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "freeAgents",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "Who can I affordably sign to fix a team need?",
    requiredEvidence: [
      "FreeAgentRecommendationModel",
      "market-state-hero",
      "recommended-signings",
      "filter-sheet",
      "offer-return-state",
    ],
    requiredStates: ["blocked", "empty", "completed", "returnContext"],
    route: ["free_agents"],
    shellMode: "standard",
    template: "marketRecommendation",
  },
  gameLog: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "games",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "gameLog",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "Why did this game end this way?",
    requiredEvidence: [
      "GameNarrativeSummary",
      "result-hero",
      "three-key-differences",
      "key-player-cards",
      "advanced-box-score-tabs",
    ],
    requiredStates: ["empty", "completed", "returnContext"],
    route: ["game_log"],
    shellMode: "standard",
    template: "postgameNarrative",
  },
  history: {
    auditOwner: "P3V1",
    bottomNav: true,
    depthPolicy: "storyFirstAdvancedArchive",
    disallowedMobilePatterns: [
      "nativeSelect",
      "firstScreenDataTable",
      "genericFieldStack",
      "equalWeightLongCards",
      "unscopedSearchOverlay",
      "unmanagedEditControls",
      "untranslatedEventText",
    ],
    domain: "archive",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "history",
    primaryActionPolicy: "readonlyContext",
    productQuestion: "Whose season was this, and what made it memorable?",
    requiredEvidence: [
      "champion-hero",
      "finals-mvp",
      "season-story",
      "major-awards-hierarchy",
      "all-league-layout",
      "complete-history-archive",
    ],
    requiredStates: ["empty", "readonly", "advancedArchive", "returnContext"],
    route: ["history"],
    shellMode: "standard",
    template: "seasonStory",
  },
  leaders: {
    auditOwner: "P3V1",
    bottomNav: true,
    depthPolicy: "storyFirstAdvancedArchive",
    disallowedMobilePatterns: [
      "nativeSelect",
      "firstScreenDataTable",
      "genericFieldStack",
      "equalWeightLongCards",
      "unscopedSearchOverlay",
      "unmanagedEditControls",
      "untranslatedEventText",
    ],
    domain: "ranking",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "leaders",
    primaryActionPolicy: "readonlyContext",
    productQuestion: "Who leads the league, and where do my players show up?",
    requiredEvidence: [
      "popular-leader-entry-cards",
      "user-team-highest-rank",
      "compact-top-rows",
      "top-three-emphasis",
      "advanced-stat-selector",
    ],
    requiredStates: ["empty", "advancedArchive", "returnContext"],
    route: ["leaders"],
    shellMode: "standard",
    template: "leaderHub",
  },
  leagueDashboard: {
    auditOwner: "P0C",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [],
    domain: "home",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "leagueDashboard",
    primaryActionPolicy: "single",
    productQuestion: "What must I handle next, and why now?",
    requiredEvidence: ["HeroTask", "single-primary-action", "blocking-tasks"],
    requiredStates: [],
    route: [],
    shellMode: "standard",
    template: "taskHub",
  },
  liveGame: {
    auditOwner: "P0C",
    bottomNav: false,
    depthPolicy: "default",
    disallowedMobilePatterns: [],
    domain: "games",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "liveGame",
    primaryActionPolicy: "immersiveControls",
    productQuestion: "What is happening in this game, and how do I control it?",
    requiredEvidence: [
      "fullscreen-shell",
      "score-hero",
      "situational-state",
      "text-controls",
      "recent-events",
      "key-players",
      "exit-confirmation",
      "postgame-actions",
    ],
    requiredStates: [],
    route: ["live_game"],
    shellMode: "fullscreen",
    template: "matchday",
  },
  marketHub: {
    auditOwner: "P0C",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "duplicatePrimaryCTA",
      "firstScreenNavigationList",
      "expandedSecondaryActions",
    ],
    domain: "market",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "marketHub",
    primaryActionPolicy: "singleSolidCTA",
    productQuestion: "What market task must I handle now, why, and where do I act?",
    requiredEvidence: [
      "stable-market-route",
      "current-task-title-and-reason",
      "three-resource-summary",
      "default-closed-other-actions",
    ],
    requiredStates: [],
    route: ["market"],
    shellMode: "standard",
    template: "taskHub",
  },
  negotiationList: {
    auditOwner: "P2V2",
    bottomNav: true,
    depthPolicy: "summaryFirstAdvancedSecondLevel",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
      "firstScreenDataTable",
      "duplicateTerminalAction",
      "unscopedSearchOverlay",
    ],
    domain: "marketObligation",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "negotiationList",
    primaryActionPolicy: "singleContextualAction",
    productQuestion:
      "Which contract decision must be made before the deadline, and what is the risk?",
    requiredEvidence: [
      "deadline-groups",
      "affordability-summary",
      "walk-away-consequence",
      "one-contract-action",
    ],
    requiredStates: ["empty", "blocked", "completed", "returnContext", "deadline"],
    route: ["negotiation"],
    shellMode: "standard",
    template: "deadlineDecision",
  },
  newLeague: {
    auditOwner: "P1V4",
    bottomNav: false,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "outsideLeague",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "newLeague",
    primaryActionPolicy: "singleSolidCTA",
    productQuestion: "Can I create a playable league in three clear decisions?",
    requiredEvidence: [
      "NewLeagueWizard",
      "ModeStep",
      "TeamStep",
      "ReviewStep",
      "AdvancedSetupSheet",
      "basicSetup",
      "advancedSetup",
    ],
    requiredStates: ["blocked", "completed", "returnContext"],
    route: ["new_league"],
    shellMode: "outsideLeague",
    template: "wizard",
  },
  news: {
    auditOwner: "P3V1",
    bottomNav: true,
    depthPolicy: "storyFirstAdvancedArchive",
    disallowedMobilePatterns: [
      "nativeSelect",
      "firstScreenDataTable",
      "genericFieldStack",
      "equalWeightLongCards",
      "unscopedSearchOverlay",
      "unmanagedEditControls",
      "untranslatedEventText",
    ],
    domain: "news",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "news",
    primaryActionPolicy: "readonlyContext",
    productQuestion: "Which league events matter to my team right now?",
    requiredEvidence: [
      "team-headline",
      "date-groups",
      "relevance-score",
      "importance-tier",
      "localized-event-text",
      "ordinary-news-lower-weight",
    ],
    requiredStates: ["empty", "dateGroup", "filterDepth", "returnContext"],
    route: ["news"],
    shellMode: "standard",
    template: "newsRelevance",
  },
  player: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "player",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "player",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "What is this player's role, risk, and best action in context?",
    requiredEvidence: [
      "PlayerDecisionSummary",
      "source-aware-primary-action",
      "role-fit-risk-summary",
      "localized-overview-tabs",
      "mobile-stats-summary",
    ],
    requiredStates: ["readonly", "sourceContext", "returnContext"],
    route: ["player"],
    shellMode: "standard",
    template: "playerDecision",
  },
  playerRatings: {
    auditOwner: "P3V1",
    bottomNav: true,
    depthPolicy: "storyFirstAdvancedArchive",
    disallowedMobilePatterns: [
      "nativeSelect",
      "firstScreenDataTable",
      "genericFieldStack",
      "equalWeightLongCards",
      "unscopedSearchOverlay",
      "unmanagedEditControls",
      "untranslatedEventText",
    ],
    domain: "ranking",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "playerRatings",
    primaryActionPolicy: "readonlyContext",
    productQuestion: "Who are the best players, and how close are my players?",
    requiredEvidence: [
      "top-player-summary",
      "user-team-rating-marker",
      "compact-ranking-row",
      "top-25-default",
      "seven-to-eight-visible-rows",
      "advanced-table-depth",
    ],
    requiredStates: ["empty", "hiddenRatings", "advancedArchive", "returnContext"],
    route: ["player_ratings"],
    shellMode: "standard",
    template: "ratingRanking",
  },
  playerStats: {
    auditOwner: "P3V1",
    bottomNav: true,
    depthPolicy: "storyFirstAdvancedArchive",
    disallowedMobilePatterns: [
      "nativeSelect",
      "firstScreenDataTable",
      "genericFieldStack",
      "equalWeightLongCards",
      "unscopedSearchOverlay",
      "unmanagedEditControls",
      "untranslatedEventText",
    ],
    domain: "stats",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "playerStats",
    primaryActionPolicy: "readonlyContext",
    productQuestion: "What is the data story for this season and my roster?",
    requiredEvidence: [
      "season-data-story",
      "user-team-highlights",
      "league-leading-metrics",
      "trend-or-change-insight",
      "grouped-stat-selector",
      "advanced-table-depth",
    ],
    requiredStates: ["empty", "hiddenRatings", "advancedArchive", "returnContext"],
    route: ["player_stats"],
    shellMode: "standard",
    template: "playerStatInsight",
  },
  playoffs: {
    auditOwner: "P2V2",
    bottomNav: true,
    depthPolicy: "summaryFirstAdvancedSecondLevel",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
      "firstScreenDataTable",
      "duplicateTerminalAction",
      "unscopedSearchOverlay",
    ],
    domain: "playoffs",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "playoffs",
    primaryActionPolicy: "readonlyNextStep",
    productQuestion: "What is my current series, next game, and path to the title?",
    requiredEvidence: [
      "user-series-first",
      "next-game-context",
      "championship-path",
      "compact-bracket-depth",
    ],
    requiredStates: ["empty", "readonly", "returnContext", "advancedDepth"],
    route: ["playoffs"],
    shellMode: "standard",
    template: "playoffPath",
  },
  protectPlayers: {
    auditOwner: "P2V2",
    bottomNav: true,
    depthPolicy: "summaryFirstAdvancedSecondLevel",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
      "firstScreenDataTable",
      "duplicateTerminalAction",
      "unscopedSearchOverlay",
    ],
    domain: "marketObligation",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "protectPlayers",
    primaryActionPolicy: "singleTerminalAction",
    productQuestion: "Who must I protect before the deadline, and who is exposed if I stop now?",
    requiredEvidence: [
      "fixed-slot-deadline-summary",
      "selected-risk-value",
      "exposed-player-confirmation",
      "single-submit-action",
    ],
    requiredStates: ["empty", "blocked", "completed", "returnContext", "deadline"],
    route: ["protect_players"],
    shellMode: "standard",
    template: "protectionDecision",
  },
  roster: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "team",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "roster",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "Is my roster playable, and what must I fix first?",
    requiredEvidence: [
      "RosterReadinessModel",
      "StarterUnit",
      "RotationList",
      "ReserveList",
      "roster-edit-mode",
      "other-team-readonly",
    ],
    requiredStates: ["blocked", "readonly", "editMode", "returnContext"],
    route: ["roster"],
    shellMode: "standard",
    template: "readiness",
  },
  standings: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "league",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "standings",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "Where am I ranked, and what is the playoff gap?",
    requiredEvidence: [
      "StandingsRow",
      "user-team-highlight",
      "playoff-line",
      "compact-default-fields",
      "quick-scroll",
    ],
    requiredStates: ["empty", "returnContext"],
    route: ["standings"],
    shellMode: "standard",
    template: "compactStandings",
  },
  teamFinances: {
    auditOwner: "P2V2",
    bottomNav: true,
    depthPolicy: "summaryFirstAdvancedSecondLevel",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
      "firstScreenDataTable",
      "duplicateTerminalAction",
      "unscopedSearchOverlay",
    ],
    domain: "teamRisk",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "teamFinances",
    primaryActionPolicy: "readonlyNextStep",
    productQuestion: "Does cap, tax, or future salary block my next roster move?",
    requiredEvidence: [
      "cap-health-summary",
      "support-metrics",
      "current-actions",
      "advanced-contract-table",
    ],
    requiredStates: ["readonly", "returnContext", "advancedDepth"],
    route: ["team_finances"],
    shellMode: "standard",
    template: "financeHealth",
  },
  teamStats: {
    auditOwner: "P3V1",
    bottomNav: true,
    depthPolicy: "storyFirstAdvancedArchive",
    disallowedMobilePatterns: [
      "nativeSelect",
      "firstScreenDataTable",
      "genericFieldStack",
      "equalWeightLongCards",
      "unscopedSearchOverlay",
      "unmanagedEditControls",
      "untranslatedEventText",
    ],
    domain: "stats",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "teamStats",
    primaryActionPolicy: "readonlyContext",
    productQuestion: "Where is my team strong or weak compared to the league?",
    requiredEvidence: [
      "team-strengths-hero",
      "top-three-strengths",
      "top-three-weaknesses",
      "key-metric-ranks",
      "compact-team-ranking",
      "advanced-table-depth",
    ],
    requiredStates: ["empty", "advancedArchive", "returnContext"],
    route: ["team_stats"],
    shellMode: "standard",
    template: "teamStatInsight",
  },
  trade: {
    auditOwner: "P1V4",
    bottomNav: true,
    depthPolicy: "default",
    disallowedMobilePatterns: [
      "nativeSelect",
      "smallStepArrows",
      "glyphicons",
      "genericSixFieldCards",
      "globalScoreStrip",
      "globalFixedCTA",
      "duplicatePrimaryCTA",
    ],
    domain: "market",
    globalFixedCTA: false,
    globalScoreStrip: false,
    pageID: "trade",
    primaryActionPolicy: "singleContextualAction",
    productQuestion: "What problem does this trade solve, and what does it cost?",
    requiredEvidence: [
      "trade-goal-step",
      "need-matched-opponents",
      "asset-workspace",
      "TradeImpactSummary",
      "repair-suggestions",
    ],
    requiredStates: ["blocked", "completed", "returnContext"],
    route: ["trade"],
    shellMode: "standard",
    template: "tradeWorkspace",
  },
} as const satisfies Record<MobilePageID, MobilePageContract>;

export const mobilePageIDs = Object.keys(mobilePageTaskContracts) as MobilePageID[];

export const isMobilePageID = (pageID: unknown): pageID is MobilePageID =>
  typeof pageID === "string" && pageID in mobilePageTaskContracts;

export const getMobilePageContract = (pageID: unknown) =>
  isMobilePageID(pageID) ? mobilePageTaskContracts[pageID] : undefined;

// ---------------------------------------------------------------------------
// Content-first contract version ("v5Content") — the active contract system.
//
// The base contracts above carry each page's shell/routing substrate. The
// v5Content version is what every page resolves to: a page must open with
// content (content-first-screen), may keep at most one guidance line
// (single-explainer-line), and must not render narration cards (no-meta-cards).
//
// Resolution is page-level opt-in: `v5ContentContractOptIns` has one entry per
// page and `getEffectiveMobilePageContract` returns the v5 contract. A page
// without an opt-in resolves to its base contract (the "legacy" version tag),
// which `resolveEffectiveContract` still exercises for injected opt-in tables.
// ---------------------------------------------------------------------------

export type V5ContentCoreEvidence =
  | "content-first-screen"
  | "single-explainer-line"
  | "no-meta-cards";

export const v5ContentCoreEvidence = [
  "content-first-screen",
  "single-explainer-line",
  "no-meta-cards",
] as const satisfies readonly V5ContentCoreEvidence[];

export type V5ContentDisallowedMobilePattern =
  // 法则一: cards must carry content or an action, never narration about the
  // page purpose, filter state, or UI changes.
  | "metaExplainerCard"
  // 法则二: the same information unit appears at most once per page.
  | "duplicateInfoUnit"
  | "nativeSelect"
  | "firstScreenDataTable"
  | "duplicatePrimaryCTA"
  | "unscopedSearchOverlay"
  | "untranslatedEventText";

export const v5ContentDisallowedMobilePatterns = [
  "metaExplainerCard",
  "duplicateInfoUnit",
  "nativeSelect",
  "firstScreenDataTable",
  "duplicatePrimaryCTA",
  "unscopedSearchOverlay",
  "untranslatedEventText",
] as const satisfies readonly V5ContentDisallowedMobilePattern[];

export type V5ContentDepthPolicy = "contentFirstExpertDepth";

export type V5ContentPageContract = {
  auditOwner: "B1V5";
  bottomNav: boolean;
  contentEvidence: readonly string[];
  contractVersion: "v5Content";
  depthPolicy: V5ContentDepthPolicy;
  disallowedMobilePatterns: readonly V5ContentDisallowedMobilePattern[];
  domain: string;
  globalFixedCTA: false;
  globalScoreStrip: false;
  legacyContract: MobilePageContract;
  pageID: MobilePageID;
  productQuestion: string;
  requiredEvidence: readonly string[];
  requiredStates: readonly string[];
  route: readonly string[];
  shellMode: MobilePageShellMode;
  template: "contentFirst";
};

export type V5ContentContractInput = {
  // Page-specific content assertions (e.g. "standings-table-first-screen").
  // Explicit by design: the base contract's explainer-type requiredEvidence
  // entries are dropped by construction, not by filtering.
  contentEvidence: readonly string[];
  productQuestion?: string;
  requiredStates?: readonly string[];
};

export const createV5ContentContract = (
  pageID: MobilePageID,
  input: V5ContentContractInput,
): V5ContentPageContract => {
  const legacyContract = mobilePageTaskContracts[pageID];

  if (input.contentEvidence.length === 0) {
    throw new Error(
      `v5Content contract for "${pageID}" needs at least one page content evidence entry`,
    );
  }

  const requiredEvidence = [
    ...v5ContentCoreEvidence,
    ...input.contentEvidence.filter(
      (evidence) => !(v5ContentCoreEvidence as readonly string[]).includes(evidence),
    ),
  ];

  return {
    auditOwner: "B1V5",
    bottomNav: legacyContract.bottomNav,
    contentEvidence: input.contentEvidence,
    contractVersion: "v5Content",
    depthPolicy: "contentFirstExpertDepth",
    disallowedMobilePatterns: v5ContentDisallowedMobilePatterns,
    domain: legacyContract.domain,
    globalFixedCTA: false,
    globalScoreStrip: false,
    legacyContract,
    pageID,
    productQuestion: input.productQuestion ?? legacyContract.productQuestion,
    requiredEvidence,
    requiredStates: input.requiredStates ?? legacyContract.requiredStates,
    route: legacyContract.route,
    shellMode: legacyContract.shellMode,
    template: "contentFirst",
  };
};

export type V5ContentContractOptIns = Partial<Record<MobilePageID, V5ContentContractInput>>;

// One opt-in entry per active mobile page (all 33). Each authors the page's
// content-first evidence; the shell/routing fields come from the base contract.
export const v5ContentContractOptIns: V5ContentContractOptIns = {
  player: {
    contentEvidence: [
      // data-v5-player-identity-first-screen: the overview tab opens with the
      // TopStuff identity card itself (photo, position/team, ratings overview,
      // status + Mood, awards summary) inside MobileContentHero; the
      // MobileManagementPage subtitle narration and the MobileDecisionHero
      // "roster decision" card (role/risk/value repeated the bio, 法则二) are
      // gone
      "identity-card-first-screen",
      // the accepted PageTabs pattern stays (overview / stats / contract) with
      // content-first tab bodies; the single source-return back nav stays as
      // the return-stack link (not a "related pages" meta button)
      "content-first-tab-bodies",
      // the awards native table is now a MobileStatRow list, and career +
      // honors stay folded behind the lazy career-depth disclosure
      "career-honors-folded-depth",
    ],
  },
  dailySchedule: {
    contentEvidence: [
      // data-v5-daily-next-game-first-screen: the hero children ARE the day's
      // matchup — the primary game ScoreBox (data-v5-daily-matchup-card) plus
      // the upcoming/completed status badges; the MobileDecisionHero title/
      // description narration is gone (法则一)
      "matchup-card-first-screen",
      // the season/day/conference filter state lives only on the TitleBar
      // dropdown; the on-page "Filtered view" summary row and the season/day/
      // conference context chips (a second and third copy of the same filter
      // state) are removed (法则二)
      "single-titlebar-filter-entry",
      // data-p1-4-daily-one-primary-cta: exactly one primary watch/sim CTA in
      // the hero action slot; the other games and the schedule links fold
      // behind the secondary depth and the date sheet
      "single-matchday-cta",
    ],
  },
  teamFinances: {
    contentEvidence: [
      // data-v5-teamfinances-cap-health-first-screen: the hero children ARE the
      // cap-health numbers — free cap space, next-season pressure, tax/limit
      // distance, and payroll as MobileStatRow content, plus projected-penalty
      // warnings and the multi-year commitment timeline. The MobileManagementPage
      // eyebrow/subtitle narration and the MobileDecisionSummary "cap health /
      // trade watch" story card (with its "you can operate" operability line and
      // the signing/trade/extension limit explainer cards) are gone (法则一).
      "cap-health-stat-rows-first-screen",
      // other-team scouting keeps a single explainer line (the scouting read) and
      // one trade-fit CTA; the budget-adjustment form + player-salary DataTable
      // fold behind the expert disclosures (readonlyNextStep policy preserved)
      "single-scouting-explainer-and-cta",
      // the historical revenue/expenses/cash tab switcher is converged into one
      // stacked history section, and the full contract table stays in the expert
      // fold (data-p2v2-finance-advanced-table)
      "contract-table-expert-depth",
    ],
  },
  playoffs: {
    contentEvidence: [
      // data-v5-playoffs-bracket-first-screen: the hero children ARE the
      // bracket — the user's own series card first (when they have one), then
      // the champion result row, then every round's series cards inline. The
      // MobileAdvancedDisclosure that used to hide the whole bracket is gone,
      // and so are the MobileDecisionSummary "playoff path" narration card and
      // the my-team-status / history-result / champion-story explainer cards
      // (法则一).
      "bracket-first-screen",
      // the single champion result is a MobileStatRow content line; the winner
      // is not re-narrated in a separate story card (法则二)
      "champion-result-row",
      // the historical/archive readonly banner keeps its return-to-current
      // control (P0-4 return stack + P0-7 archive markers stay load-bearing);
      // the old season/day matchday links fold behind the lazy expert
      // disclosure inside that banner
      "archive-readonly-return-banner",
    ],
  },
  draft: {
    contentEvidence: [
      // data-v5-draft-pick-decision-first-screen: the hero children ARE the
      // pick decision — team-need badges, the #1 recommendation expanded card,
      // and the #2/#3 compact rows (current pick status stays in the sticky
      // strip); one primary phase CTA in the hero action slot
      "pick-decision-first-screen",
      // data-v5-draft-overflow-entry: every secondary sim/trade action plus
      // the other draft views live behind ONE MobileExpertDisclosure
      "single-overflow-draft-actions",
      // data-v5-draft-full-pool-expert-depth: the rank-4+ prospect pool folds
      // behind the expert disclosure (top-3 never re-listed, 法则二)
      "full-pool-expert-depth",
    ],
  },
  draftLottery: {
    contentEvidence: [
      // G2d-1b: the hero children ARE the lottery content — the stage line plus
      // the odds / expected-pick / asset-impact probability cards as MobileStatRow
      // rows. The core-task / archive-readonly template narration and the
      // MobileDecisionSummary "lottery stage" meta card (conclusion/consequence/
      // deadline) are gone (法则一).
      "lottery-probability-cards-first-screen",
      // the season nav + one stage-explicit CTA is the single action surface; the
      // historical variant surfaces result-review and expected-vs-final inline
      // instead of a separate narration card (法则二)
      "single-season-nav-and-stage-cta",
      // the full per-team board (with the reveal state machine) and the COLA
      // chances table stay behind counted MobileExpertDisclosures
      // (data-p2v2-draft-lottery-probability-depth)
      "full-board-expert-depth",
    ],
  },
  draftScouting: {
    contentEvidence: [
      // G2d-1: the hero children ARE the rookie-class content — the class
      // overview (coverage/confidence/lanes) as MobileStatRow rows and the top
      // prospects as MobileEntityRow rows. The MobileCoreTaskPage/
      // MobileArchiveReadOnlyPage template narration and the MobileDecisionSummary
      // "team needs shortlist" meta card (conclusion/consequence/deadline) are
      // gone (法则一).
      "rookie-class-content-first-screen",
      // the season nav + segmented board filter is the single navigation surface;
      // the historical variant surfaces expected-vs-actual review inline instead
      // of a separate narration card (法则二)
      "single-season-and-board-nav",
      // the full class DataTables (recommended/shortlist/all lanes) stay behind
      // one counted MobileExpertDisclosure (data-p2v2-scouting-prospect-depth)
      "full-class-expert-depth",
    ],
  },
  freeAgents: {
    contentEvidence: [
      // data-v5-free-agents-recommended-first-screen: the open-state first
      // screen is the recommended-signings list (PlayerCardList content rows
      // inside MobileContentHero, salary/roster-gap as the one explainer line)
      "recommended-players-first-screen",
      // data-v5-free-agents-closed-first-screen: closed state is exactly the
      // A3 actionable empty state (reason + impact + one action)
      "closed-state-actionable-empty-state",
      // data-v5-signed-history-summary-first-screen: archive variant opens
      // with summary stat rows, filter-state chips removed
      "signed-history-summary-rows",
    ],
  },
  trade: {
    contentEvidence: [
      // data-v5-trade-partner-rows-first-screen: opponent step opens with the
      // roster-need stat row + recommended partner rows (no goal hero)
      "partner-rows-first-screen",
      // data-v5-trade-step-title-line: three-step wizard kept, step
      // description merged into the single step-title explainer line
      "three-step-wizard-single-step-line",
      // data-v5-trade-impact-summary-first-screen: review step opens with the
      // impact summary itself (no consequences narration alert)
      "impact-summary-review-first",
    ],
  },
  marketHub: {
    contentEvidence: [
      // data-v5-market-primary-task: current market task title + reason lead
      // the first screen before resources and the single primary CTA
      "market-primary-task-first",
      // data-v5-market-resource-stat-rows: cap space / roster gap / market
      // status as plain MobileStatRow rows (no detail sentences, no badges)
      "market-resource-stat-rows",
      // data-v5-market-single-cta: exactly one primary market CTA
      "single-market-cta",
      // data-market-hub-more-actions-default-closed: deduplicated secondary and
      // unavailable market routes remain in the closed disclosure by default
      "default-closed-market-actions",
    ],
  },
  negotiationList: {
    contentEvidence: [
      // data-v5-negotiation-pending-first-screen: decisions/asking/cap-space
      // stat rows + the grouped pending-negotiation player list itself open
      // the page; one deadline explainer line; single primary CTA (jump to
      // the must-handle group) only while required decisions remain
      "pending-negotiations-first-screen",
      // empty phase = the A3 actionable empty state (reason + impact + one
      // continue-season action)
      "empty-state-single-action",
      // rules/cap details stay behind the lazy expert disclosure
      "rules-expert-depth",
    ],
  },
  news: {
    contentEvidence: [
      // data-v5-news-team-first-screen: the hero children ARE the news — top
      // story + own-team latest 3 (data-v5-news-team-latest) + league headline
      // 1 (data-v5-news-league-headline), mutually-exclusive groups (法则二)
      "team-first-news-hero",
      // segment remainders, ordinary updates, and timeline overflow fold
      // behind lazy disclosures; the season timeline paginates
      // (data-v5-news-timeline-overflow)
      "folded-feed-pagination",
      // filter state lives on the single sheet trigger; the chip row moved
      // into the sheet body (no filter-state cards on the page, 法则一)
      "single-filter-entry",
    ],
  },
  roster: {
    contentEvidence: [
      // data-v5-roster-status-first-screen: health/rotation status as
      // MobileStatRow rows (players / healthy / salary space), one explainer
      // line only when the readiness model needs attention
      "health-status-stat-rows",
      // data-v5-roster-starting-five: the starting five as MobileEntityRow
      // rows inside the hero (media slot reserved for C2), listed exactly once
      "starting-five-entity-rows",
      // the complete roster stays behind the folded depth with the single
      // fixed management CTA (data-p0-8-roster-fixed-management-action)
      "full-roster-folded-single-cta",
    ],
  },
  teamStats: {
    contentEvidence: [
      // data-v5-teamstats-ranking-first-screen: hero children are the compact
      // team ranking rows (MobileEntityRow, user team highlighted)
      "team-ranking-first-screen",
      // own-team strengths/weaknesses as MobileStatRow content (A3-gated)
      "strength-weakness-stat-rows",
      // complete league table behind the expert disclosure
      "full-table-expert-depth",
    ],
  },
  playerRatings: {
    contentEvidence: [
      // G2e: the hero children ARE the rating board — the top-rated players as
      // MobileEntityRow rows (user team highlighted) plus the A3-gated
      // best-team-rating stat row. The MobileDataStoryPage eyebrow/subtitle and
      // the MobileArchiveHero description narration (which re-explained the board
      // layout) are gone (法则一); only the single scope explainer line remains.
      "rating-board-first-screen",
      // the starter/trade-value/potential role badges plus the league-elite and
      // obtainable-target markers stay as content, not a separate story card (法则二)
      "role-and-target-content",
      // the complete ratings DataTable stays behind one counted
      // MobileExpertDisclosure (data-p1-7-ratings-full-board-folded)
      "full-table-expert-depth",
    ],
  },
  playerStats: {
    contentEvidence: [
      // data-v5-playerstats-leader-rows-first-screen: hero children are the
      // league-leading metric rows (MobileEntityRow)
      "leader-rows-first-screen",
      // A3 gate: accumulating line / empty reason instead of 0-value rows
      "stat-meaningfulness-gate",
      // P2S-6 continuity: advanced modes keep one explanation line (the hero
      // explainer) plus 2-3 key metrics; complete table in expert depth
      "advanced-expert-depth",
    ],
  },
  leaders: {
    contentEvidence: [
      // data-v5-leaders-top-list-first-screen: hero children are the top-3
      // rows per popular category (MobileEntityRow)
      "leader-top-rows-first-screen",
      // A3 meaningfulness gate stays wired (accumulating line / empty state)
      "stat-meaningfulness-gate",
      // full tables + eligibility notes behind the expert disclosure
      "full-tables-expert-depth",
    ],
  },
  standings: {
    contentEvidence: [
      // data-v5-standings-table-first-screen: the hero children ARE the table
      "standings-table-first-screen",
      // data-standings-user-team highlight
      "user-team-row-anchor",
      // Title-bar filter keeps only overall/conference ranking scopes.
      "overall-conference-switch",
    ],
  },
  // C3: live game. Fullscreen shellMode and productQuestion come from the base
  // contract above.
  liveGame: {
    contentEvidence: [
      // data-v5-live-game-score-hero: the mobile first screen opens with the
      // score itself — both logos, large tabular score, period clock, and a
      // possession dot, tinted with the teams' primary colors (C2 color-mix
      // precedent).
      "score-hero-first-screen",
      // data-v5-live-game-quarter-scores: per-period score strip from the
      // ptsQtrs data already on the live box score.
      "quarter-score-strip",
      // data-mobile-live-key-play-weight: play-by-play entries carry a
      // 0-3 weight (clutch/lead-change/big-run/gameOver = 3; threes, dunk
      // family, blocks = 2); the gamecast feed shows key plays + the newest
      // few, the full stream stays behind the play-by-play section.
      "weighted-key-plays",
      // data-v5-live-game-settings-sheet: the old 更多控制 / 更多比赛信息
      // folds merged into ONE lazy-mounted settings/info sheet; the fixed
      // bottom playback control stays the single primary control and the
      // speed presets sit on the always-visible control row.
      "single-settings-sheet",
    ],
  },
  // C1: home page. productQuestion ("What must I handle next, and why now?")
  // comes from the base contract above.
  leagueDashboard: {
    contentEvidence: [
      // data-v5-dashboard-scoreboard-first-screen: basketball context remains
      // in the priority-ordered first-screen slots — a real task/day-done may
      // lead it, while today's matchday and fallback states let it lead. Cards
      // without data skip gracefully.
      "scoreboard-first-screen",
      // data-v5-dashboard-phase-task-line: resolveDashboardPrimaryTask()
      // demoted to one line + one CTA (data-p0c-dashboard-primary-task); the
      // hero card group / eyebrow / description / impact narration are gone.
      "single-phase-task-line",
      // U2-2: data-v5-dashboard-status-summary answers goal / progress /
      // strategic risk in one compact first-screen unit. Optional owner trust
      // is title + tier + in-place explanation sheet; only hot-seat adds a
      // warning. Operational risk remains in the focus/queue.
      "compact-season-status-summary",
    ],
  },
  gameLog: {
    contentEvidence: [
      // data-v5-game-log-recent-first-screen: with no box score selected the
      // hero children ARE the recent games list (MobileEntityRow rows) plus the
      // single "view latest box score" CTA; with a game selected the
      // BoxScoreWrapper is the first content. The MobileCoreTaskPage eyebrow/
      // subtitle narration and the MobileDecisionHero score-commentary story
      // card (result label + score + key player + three key-difference badges —
      // the score itself is content shown by the box score, 法则一/法则二) are
      // gone.
      "recent-games-first-screen",
      // data-p1-4-game-log-box-score-entry: the selected game's box score is the
      // first content, with no "back to game story" return link (no story card)
      "box-score-first-content",
      // the full team game list folds behind the lazy list depth
      // (data-p1-4-game-log-list-depth); the read-only archive badge stays
      "full-list-expert-depth",
    ],
  },
  history: {
    contentEvidence: [
      // data-champion-hero: the champion hero is content in a MobileContentHero
      // (champion title + Finals MVP + the award winners as MobileStatRow rows);
      // the MobileArchiveHero surface narration and the MobileInsightStrip
      // explainer strip are gone — the award rows carry the real conclusions
      // (法则一, InsightStrip → StatRow per b1 map).
      "champion-awards-first-screen",
      // the season-story timeline (MobileTimeline) and the archive season nav
      // (MobileArchiveSeasonNav) stay as content-navigation (retained primitives)
      "season-story-timeline-and-nav",
      // the complete-season archive (awards / all-league teams / retired players)
      // folds behind the p5 expert disclosure (MobileAdvancedArchiveDisclosure →
      // MobileExpertDisclosure), keeping data-complete-history-archive +
      // data-p3v1-return-context="history"
      "complete-archive-expert-depth",
    ],
  },
  draftHistory: {
    contentEvidence: [
      // data-recent-class-summary: the first screen IS the draft-class story in
      // a MobileContentHero — best pick / biggest steal / top-pick miss (the
      // draftHistoryStory.ts conclusions) plus your-picks and class-size as
      // MobileStatRow rows (data-best-pick / -biggest-steal / -underperformer),
      // with the class summary as the single explainer line. The
      // MobileArchiveHero surface + "Draft class story" eyebrow narration and
      // the MobileInsightStrip explainer strip are gone (法则一, InsightStrip →
      // StatRow per b1 map); the A3 count-0 gate on your-picks stays.
      "draft-story-first-screen",
      // the incomplete-draft state is a standard MobileActionableEmptyState
      // (reason + impact + one primary action, MobileP2SActionableEmptyState →
      // MobileActionableEmptyState), and season browsing / draft-center return
      // fold into the bottom-sheet secondary archive depth
      "standard-empty-state-and-year-sheet",
      // the full draft / current / peak / career DataTable folds behind the p5
      // expert disclosure (MobileAdvancedArchiveDisclosure → MobileExpertDisclosure),
      // keeping data-advanced-table-depth + data-p3v1-return-context="draftHistory"
      "full-table-expert-depth",
    ],
  },
  dashboard: {
    contentEvidence: [
      // data-v5-dashboard-save-first-screen: the outside-league entry opens with
      // the continue-career save card itself (league name, team, phase, last
      // played inside MobileContentHero) — the MobileDecisionHero narration card
      // and its "Next risk: {phase}" / "Usually 2 min" PM badges (法则一) are gone.
      "save-cards-first-screen",
      // data-p1-2-dashboard-new-challenge-card: exactly one new-challenge entry
      // card with the single primary "start" CTA; the "Next: choose team" /
      // "Risk: opening roster" PM badges are removed, only the mode badge stays
      "new-challenge-single-entry",
      // the recent-league grid and the full "All leagues" list fold behind the
      // library section and the details disclosure (data-dashboard-all-leagues)
      "league-library-folded-depth",
    ],
  },
  protectPlayers: {
    contentEvidence: [
      // data-v5-protect-players-profile: the MobileCoreTaskPage eyebrow/subtitle
      // narration ("Fill protection slots, inspect unprotected risk…" — the
      // "scope is informational" readonly subtitle) is gone (法则一); the page
      // wrapper is a header-less MobileContentHero and the first content is the
      // terminal-action decision card (retained MobileDecisionSummary, the
      // singleTerminalAction confirm-before-expose semantics stay).
      "protection-decision-first-screen",
      // data-p1-6-protect-slot-counts: the remaining-slots / max-per-team counts
      // are MobileStatRow content; the must / suggested / expendable grouped
      // player cards are the selection body (data-p1-6-protect-risk-group)
      "slot-count-stat-rows-and-grouped-list",
      // the full protect DataTable folds behind the expert disclosure
      // (data-p2v2-protect-advanced-table, MobileExpertDisclosure retained)
      "full-table-expert-depth",
    ],
  },
  newLeague: {
    contentEvidence: [
      // data-p1-2-page-02: each wizard step (mode / team / review) opens with its
      // content inside a header-less MobileContentHero — the MobileCoreTaskPage
      // eyebrow (a duplicate of the progress capsule) and step title (法则二) are
      // gone, only the single explainer line survives (法则一). The mode cards,
      // team cards, and opening-preview are the first content on each screen.
      "wizard-step-content-first-screen",
      // data-new-league-recommended-teams: recommended teams lead, the full team
      // browser folds behind data-p1-2-new-league-team-browse-depth, and advanced
      // setup stays folded behind the expert path (data-p0-6-new-league-expert-path)
      "recommended-first-folded-depth",
      // MobileActionStack → MobileDecisionBar for the sticky CTAs; the "Next:
      // choose team" / "Next: confirm challenge" / "Budget risk:" PM prefixes are
      // removed (data-p1-2-new-league-fixed-primary-cta stays as the fixed CTA)
      "single-decision-bar-cta",
    ],
  },
};

export const v5ContentPageContracts = Object.fromEntries(
  (Object.entries(v5ContentContractOptIns) as [MobilePageID, V5ContentContractInput][]).map(
    ([pageID, input]) => [pageID, createV5ContentContract(pageID, input)],
  ),
) as Partial<Record<MobilePageID, V5ContentPageContract>>;

export const v5ContentPageIDs = Object.keys(v5ContentPageContracts) as MobilePageID[];

export const isV5ContentPageID = (pageID: unknown): pageID is MobilePageID =>
  typeof pageID === "string" && pageID in v5ContentPageContracts;

export type MobileEffectiveContractVersion = "legacy" | "v5Content";

export type MobileEffectiveContract =
  | {
      contract: MobilePageContract;
      pageID: MobilePageID;
      version: "legacy";
    }
  | {
      contract: V5ContentPageContract;
      pageID: MobilePageID;
      version: "v5Content";
    };

// Pure resolver for tests and audit tooling: inject any opt-in table.
export const resolveEffectiveContract = (
  pageID: unknown,
  optIns: V5ContentContractOptIns,
): MobileEffectiveContract | undefined => {
  if (!isMobilePageID(pageID)) {
    return undefined;
  }

  const optIn = optIns[pageID];

  if (optIn) {
    return {
      contract: createV5ContentContract(pageID, optIn),
      pageID,
      version: "v5Content",
    };
  }

  return {
    contract: mobilePageTaskContracts[pageID],
    pageID,
    version: "legacy",
  };
};

// Single resolution point for the app and tests: the page's v5 contract when it
// has opted in, its base contract otherwise.
export const getEffectiveMobilePageContract = (
  pageID: unknown,
): MobileEffectiveContract | undefined => {
  if (!isMobilePageID(pageID)) {
    return undefined;
  }

  const v5Contract = v5ContentPageContracts[pageID];

  if (v5Contract) {
    return {
      contract: v5Contract,
      pageID,
      version: "v5Content",
    };
  }

  return {
    contract: mobilePageTaskContracts[pageID],
    pageID,
    version: "legacy",
  };
};
